import produce, { immerable, enableMapSet, isDraft } from 'immer';
import { clone, set } from 'lodash';

export { isMatch, isMatchId } from '@mssgme/helpers';

enableMapSet();

export function createFuncReducer(initialState, handle) {
    return (state = initialState, action) => produce(state, (draft) => handle(draft, action));
}

export function reduceReducers(...reducers) {
    return (state, action) => {
        return reducers.reduce((acc, reducer) => {
            return reducer(acc, action);
        }, state);
    };
}

export function createActionCreator(type, produceAction) {
    const creator = (...args) => {
        if (produceAction) return { type, ...produceAction(...args) };
        const [payload, ...rest] = args;
        return { type, payload, meta: rest };
    };
    creator.type = type;
    creator.toString = () => type;
    return creator;
}

export const createAction = createActionCreator;

export class ReducerBuilder {
    constructor(state, opts) {
        opts = {
            immutable: true,
            ...opts,
        };
        this._state = state;
        this._immutable = opts.immutable;
        this._cases = {};
    }

    addCase(types, handler) {
        if (Array.isArray(types)) {
            for (let type of types) {
                this._cases[String(type)] = handler;
            }
        } else {
            this._cases[String(types)] = handler;
        }

        return this;
    }

    hasCase(type) {
        // eslint-disable-next-line no-prototype-builtins
        return this._cases.hasOwnProperty(type);
    }

    runCase(type, ...args) {
        return this._cases[type](...args);
    }

    createReducer() {
        let reducer;

        if (this._immutable) {
            reducer = (state = this._state, action) => {
                if (isDraft(state) && this.hasCase(action.type)) {
                    return this.runCase(action.type, state, action);
                }

                return produce(state, (draft) => {
                    if (this.hasCase(action.type)) {
                        return this.runCase(action.type, draft, action);
                    }
                });
            };
        } else {
            reducer = (state = this._state, action) => {
                return this.hasCase(action.type) ? this.runCase(action.type, state, action) : state;
            };
        }

        reducer.builder = this;

        return reducer;
    }
}

export function createReducerBuilder(initialState, handler, opts) {
    const builder = new ReducerBuilder(initialState, opts);

    if (typeof handler === 'function') {
        handler(builder);
    } else if (typeof handler === 'object') {
        for (const prop of Object.keys(handler)) {
            builder.addCase(prop, handler[prop]);
        }
    } else {
        throw new Error('Invalid handler type');
    }

    return builder;
}

export function createReducer(initialState, handler, opts) {
    if (typeof handler === 'function' && handler.length === 2) {
        return createFuncReducer(initialState, handler);
    } else {
        const builder = createReducerBuilder(initialState, handler, opts);
        return builder.createReducer();
    }
}

export class Collection {
    [immerable] = true;

    constructor(arr = [], identifier = '_id') {
        this._identifier = identifier;
        if (arr instanceof Collection) {
            arr = Collection.toArray();
        }
        this.ids = arr.map((item) => item[identifier]);
        this.byIds = arr.reduce((acc, item) => {
            acc[item[identifier]] = item;
            return acc;
        }, {});
    }

    get length() {
        return this.ids.length;
    }

    add(items) {
        if (!Array.isArray(items)) items = [items];

        for (let item of items) {
            this.byIds[item[this._identifier]] = item;
            if (!this.exists(item)) {
                this.ids.push(item[this._identifier]);
            }
        }

        return this;
    }

    update(path, data) {
        set(this.byIds, path, data);
        return this;
    }

    exists(item) {
        return this.ids.indexOf(item[this._identifier]) >= 0;
    }

    remove(item) {
        this.ids = this.ids.filter((id) => id !== item[this._identifier]);
        delete this.ids[item[this._identifier]];

        return this;
    }

    removeById(itemId) {
        this.ids = this.ids.filter((id) => id !== itemId);
        delete this.ids[itemId];

        return this;
    }

    find(itemId) {
        return this.byIds[itemId];
    }

    clear() {
        this.ids = [];
        this.byIds = {};
        return this;
    }

    isEmpty() {
        return this.length === 0;
    }

    toArray() {
        return this.ids.map((id) => this.byIds[id]);
    }

    forEach(callback) {
        this.toArray().forEach(callback);
    }

    filter(callback) {
        return new Collection(this.toArray().forEach(callback));
    }

    map(callback) {
        return this.toArray().map(callback);
    }

    clone() {
        return clone(this);
    }
}

export function updateIn(arr, cond, val) {
    const index = arr.findIndex(cond);
    if (index >= 0) arr[index] = val;

    return index >= 0;
}

export function deleteIn(arr, cond) {
    const index = arr.findIndex(cond);
    if (index >= 0) return arr.splice(index, 1)[0];
}
